import { useEffect, useMemo, useState } from "react";
import { Package, RotateCcw } from "@/components/icons";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  Input,
  Skeleton,
} from "@/components/kit";
import { useIsMobile } from "@/hooks/use-mobile";
import { useVariantStockLotsQuery } from "@/modules/inventory/presentation/api/queries";
import { formatQtyWithUnit } from "@/modules/products/domain/unit-conversion";
import { formatLak } from "@/shared/lib/currency";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import {
  buildFifoLotPicks,
  type OrderLineLotPick,
  sumLotPickQuantity,
} from "../lib/order-lot-allocation";

type OrderLotPickerSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variantId: string;
  variantName: string;
  productName: string;
  stockUnitLabel: string;
  unitsPerSale?: number;
  quantity: number;
  isBonus?: boolean;
  initialPicks?: OrderLineLotPick[];
  onConfirm: (picks: OrderLineLotPick[]) => void;
};

function LotPickerContent({
  variantId,
  variantName,
  productName,
  stockUnitLabel,
  unitsPerSale = 1,
  quantity,
  isBonus,
  initialPicks,
  onConfirm,
  onCancel,
}: Omit<OrderLotPickerSheetProps, "open" | "onOpenChange"> & {
  onCancel: () => void;
}) {
  const lots = useVariantStockLotsQuery(variantId);
  const rows = lots.data?.data ?? [];
  const [picks, setPicks] = useState<OrderLineLotPick[]>([]);
  const poolUnitLabel =
    rows[0] && unitsPerSale > 1 ? stockUnitLabel : stockUnitLabel;

  useEffect(() => {
    if (!lots.data) return;
    if (
      initialPicks &&
      initialPicks.length > 0 &&
      sumLotPickQuantity(initialPicks) === quantity
    ) {
      setPicks(initialPicks);
      return;
    }
    setPicks(buildFifoLotPicks(lots.data.data, quantity, unitsPerSale));
  }, [lots.data, initialPicks, quantity, unitsPerSale]);

  const allocated = sumLotPickQuantity(picks);
  const isValid = rows.length === 0 || allocated === quantity;
  const lotMap = useMemo(
    () => new Map(rows.map((lot) => [lot.id, lot])),
    [rows],
  );

  function resetFifo() {
    setPicks(buildFifoLotPicks(rows, quantity, unitsPerSale));
  }

  function maxSaleFromLot(lotRemaining: number) {
    return Math.floor(lotRemaining / Math.max(unitsPerSale, 1));
  }

  function setLotQuantity(stockLotId: string, nextQty: number) {
    const lot = lotMap.get(stockLotId);
    if (!lot) return;
    const maxSale = maxSaleFromLot(lot.quantityRemaining);
    const qty = Math.max(0, Math.min(nextQty, maxSale, quantity));
    setPicks((prev) => {
      const others = prev.filter((pick) => pick.stockLotId !== stockLotId);
      if (qty <= 0) return others;
      return [
        ...others,
        {
          stockLotId: lot.id,
          quantity: qty,
          receiptId: lot.receiptId,
          receiptNumber: lot.receiptNumber,
          quantityRemaining: lot.quantityRemaining,
          unitLandedCost: lot.unitLandedCost,
        },
      ];
    });
  }

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="rounded-xl border border-secondary/20 bg-background/60 p-3 text-sm">
          <p className="font-medium">{productName}</p>
          <p className="text-muted-foreground text-xs">
            {variantName} · {formatQtyWithUnit(quantity, stockUnitLabel)}
            {isBonus ? " · ແຖມ" : ""}
          </p>
          {unitsPerSale > 1 ? (
            <p className="mt-1 text-muted-foreground text-xs">
              1 {stockUnitLabel} = {unitsPerSale} ຫນ່ວຍຄລັງ/lot
            </p>
          ) : null}
          <p className="mt-2 text-xs">
            ເລືອກ lot{" "}
            <span
              className={
                isValid
                  ? "font-semibold text-leaf"
                  : "font-semibold text-destructive"
              }
            >
              {allocated}/{quantity}
            </span>
          </p>
        </div>

        {lots.isLoading ? (
          <Skeleton className="h-32 rounded-xl" />
        ) : rows.length === 0 ? (
          <p className="rounded-xl border border-dashed p-4 text-center text-muted-foreground text-sm">
            ບໍ່ມີ lot — COGS ອາດ = 0 ຕອນຂາຍ
          </p>
        ) : (
          <ul className="flex max-h-[min(50dvh,20rem)] flex-col gap-2 overflow-y-auto">
            {rows.map((lot) => {
              const pick = picks.find((row) => row.stockLotId === lot.id);
              const value = pick?.quantity ?? 0;
              const maxSale = maxSaleFromLot(lot.quantityRemaining);
              return (
                <li
                  key={lot.id}
                  className="rounded-xl border border-border/60 bg-muted/20 p-3"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Package className="size-3.5 shrink-0 text-muted-foreground" />
                        <span className="truncate font-medium text-sm">
                          {lot.receiptNumber}
                        </span>
                      </div>
                      <p className="mt-0.5 text-muted-foreground text-xs">
                        {formatDateTimeLocal(lot.receivedAt)} · ຄົງ{" "}
                        {formatQtyWithUnit(
                          lot.quantityRemaining,
                          poolUnitLabel,
                        )}
                        {unitsPerSale > 1
                          ? ` (≈ ${maxSale} ${stockUnitLabel})`
                          : null}{" "}
                        · {formatLak(lot.unitLandedCost)}/{poolUnitLabel}
                      </p>
                    </div>
                    <Input
                      type="number"
                      min={0}
                      max={Math.min(maxSale, quantity)}
                      value={value}
                      onChange={(e) =>
                        setLotQuantity(lot.id, Number(e.target.value) || 0)
                      }
                      className="h-9 w-20 tabular-nums"
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start rounded-full"
          onClick={resetFifo}
          disabled={rows.length === 0}
        >
          <RotateCcw data-icon="inline-start" />
          FIFO (ເກົ່າກ່ອນ)
        </Button>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          className="rounded-full"
          onClick={onCancel}
        >
          ຍົກເລີກ
        </Button>
        <Button
          type="button"
          className="rounded-full"
          disabled={!isValid}
          onClick={() => onConfirm(picks)}
        >
          ຢືນຢັນ lot
        </Button>
      </div>
    </>
  );
}

export function OrderLotPickerSheet({
  open,
  onOpenChange,
  onConfirm,
  ...props
}: OrderLotPickerSheetProps) {
  const isMobile = useIsMobile();

  const handleConfirm = (picks: OrderLineLotPick[]) => {
    onConfirm(picks);
    onOpenChange(false);
  };

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="flex max-h-[92dvh] flex-col gap-0 px-4 pb-4">
          <DrawerHeader className="px-0 text-left">
            <DrawerTitle className="font-display text-lg">
              ເລືອກ lot
            </DrawerTitle>
          </DrawerHeader>
          <LotPickerContent
            {...props}
            onConfirm={handleConfirm}
            onCancel={() => onOpenChange(false)}
          />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90dvh,640px)] flex-col gap-4 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">ເລືອກ lot</DialogTitle>
        </DialogHeader>
        <LotPickerContent
          {...props}
          onConfirm={handleConfirm}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
